<template>
    <div>
        <progress-header />

        <div class="container mt-4">
            <form @submit.prevent="handleSubmit">
                <div class="card booking-form-card shadow-sm">
                    <div class="card-header py-3">
                        <span class="icon transfer-type-icon"></span>

                        {{ $t('btm.travel_options.header') }}
                    </div>

                    <div class="row card-content">
                        <div class="col-12 col-md-6 pr-md-0">
                            <h4 class="text-blue-600 font-weight-bold">
                                {{ $t('btm.travel_options.base_options') }}
                            </h4>

                            <div class="row px-4 py-3">
                                <div class="col-12">
                                    <div>
                                        <label for="PlaneTicketNeeded">
                                            <input id="PlaneTicketNeeded" type="checkbox" v-model="isPlaneTicketNeeded">
                                            {{ $t('btm.travel_options.plane.is_plane_ticket_needed') }}
                                        </label>
                                    </div>

                                    <div v-if="isPlaneTicketNeeded">
                                        <select name="plane_option" class="custom-select mb-3 pretty-input" v-model="localTravelOptions.plane_option">
                                            <option value="null" disabled selected hidden>{{ $t('select') }}</option>
                                            <option value="regular">{{ $t('btm.travel_options.plane.regular') }}</option>
                                            <option value="low_cost">{{ $t('btm.travel_options.plane.low_cost') }}</option>
                                            <option value="any">{{ $t('btm.travel_options.plane.any') }}</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label for="AirportTransferNeeded">
                                            <input id="AirportTransferNeeded" type="checkbox" v-model="isAirportTransferNeeded">
                                            {{ $t('btm.travel_options.transfer.is_airport_transfer_needed') }}
                                        </label>
                                    </div>

                                    <div v-if="isAirportTransferNeeded">
                                        <select name="transfer_option" class="custom-select mb-3 pretty-input" v-model="localTravelOptions.transfer_option">
                                            <option value="null" disabled selected hidden>{{ $t('select') }}</option>
                                            <option value="bt">{{ $t('btm.travel_options.transfer.bt') }}</option>
                                            <option value="at">{{ $t('btm.travel_options.transfer.at') }}</option>
                                            <option value="any">{{ $t('btm.travel_options.transfer.any') }}</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label for="HotelNeeded">
                                            <input id="HotelNeeded" type="checkbox" v-model="isHotelNeeded">
                                            {{ $t('btm.travel_options.hotel.is_hotel_needed') }}
                                        </label>
                                    </div>

                                    <div v-if="isHotelNeeded">
                                        <select name="hotel_stars" class="custom-select mb-3 pretty-input" v-model="localTravelOptions.hotel_option.hotel_stars">
                                            <option value="null" disabled selected hidden>{{ $t('select') }}</option>
                                            <option value="5">5 {{ $t('btm.travel_options.hotel.stars') }}</option>
                                            <option value="4">4 {{ $t('btm.travel_options.hotel.stars') }}</option>
                                            <option value="3">3 {{ $t('btm.travel_options.hotel.stars') }}</option>
                                            <option value="2">2 {{ $t('btm.travel_options.hotel.stars') }}</option>
                                            <option value="1">1 {{ $t('btm.travel_options.hotel.stars') }}</option>
                                        </select>

                                        <select name="hotel_option" class="custom-select mb-3 pretty-input" v-model="localTravelOptions.hotel_option.hotel_preference">
                                            <option value="null" disabled selected hidden>{{ $t('select') }}</option>
                                            <option value="best_price">{{ $t('btm.travel_options.hotel.best_price') }}</option>
                                            <option value="best_quality">{{ $t('btm.travel_options.hotel.best_quality') }}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="col-12 col-md-6 pl-md-0">
                            <h4 class="text-blue-600 font-weight-bold">
                                {{ $t('btm.travel_options.additional_options') }}
                            </h4>

                            <div class="row px-4 py-3">
                                <div class="col-12">
                                    <label for="FerryTicketNeeded">
                                        <input id="FerryTicketNeeded" type="checkbox" v-model="isFerryTicketNeeded">
                                        {{ $t('btm.travel_options.ferry.is_ferry_ticket_needed') }}
                                    </label>
                                    <div class="px-2 mb-3" v-if="isFerryTicketNeeded">
                                        <div class="col-12 form-column mb-2">
                                            <input
                                                type="text"
                                                name="from"
                                                class="form-control pretty-input"
                                                :placeholder="$t('from') + ':'"
                                                v-model="localTravelOptions.ferry_option.start_place"
                                            >
                                        </div>

                                        <div class="col-12 form-column">
                                            <input
                                                type="text"
                                                name="to"
                                                class="form-control pretty-input"
                                                :placeholder="$t('to') + ':'"
                                                v-model="localTravelOptions.ferry_option.end_place"
                                            >
                                        </div>
                                    </div>
                                </div>

                                <div class="col-12">
                                    <label for="TrainTicketNeeded">
                                        <input id="TrainTicketNeeded" type="checkbox" v-model="isTrainTicketNeeded">
                                        {{ $t('btm.travel_options.train.is_train_ticket_needed') }}
                                    </label>

                                    <div class="px-2 mb-3" v-if="isTrainTicketNeeded">
                                        <div class="col-12 form-column mb-2">
                                            <input
                                                type="text"
                                                name="from"
                                                class="form-control pretty-input"
                                                :placeholder="$t('from') + ':'"
                                                v-model="localTravelOptions.train_option.start_place"
                                            >
                                        </div>

                                        <div class="col-12 form-column">
                                            <input
                                                type="text"
                                                name="to"
                                                class="form-control pretty-input"
                                                :placeholder="$t('to') + ':'"
                                                v-model="localTravelOptions.train_option.end_place"
                                            >
                                        </div>
                                    </div>
                                </div>

                                <div class="col-12">
                                    <label for="CoachTicketNeeded">
                                        <input id="CoachTicketNeeded" type="checkbox" v-model="isCoachTicketNeeded">
                                        {{ $t('btm.travel_options.coach.is_coach_ticket_needed') }}
                                    </label>

                                    <div class="px-2 mb-3" v-if="isCoachTicketNeeded">
                                        <div class="col-12 form-column mb-2">
                                            <input
                                                type="text"
                                                name="from"
                                                class="form-control pretty-input"
                                                :placeholder="$t('from') + ':'"
                                                v-model="localTravelOptions.coach_option.start_place"
                                            >
                                        </div>

                                        <div class="col-12 form-column">
                                            <input
                                                type="text"
                                                name="to"
                                                class="form-control pretty-input"
                                                :placeholder="$t('to') + ':'"
                                                v-model="localTravelOptions.coach_option.end_place"
                                            >
                                        </div>
                                    </div>
                                </div>

                                <div class="col-12">
                                    <label for="isFormalAssistanceNeeded">
                                        <input id="isFormalAssistanceNeeded" type="checkbox" v-model="localTravelOptions.formal_assistance">
                                        {{ $t('btm.travel_options.formal_assistance.is_formal_assistance_needed') }}
                                    </label>
                                </div>

                                <div class="col-12">
                                    <label for="isEventNeeded">
                                        <input id="isEventNeeded" type="checkbox" v-model="localTravelOptions.event_assistance">
                                        {{ $t('btm.travel_options.event_assistance.is_event_needed') }}
                                    </label>
                                </div>

                                <div class="col-12">
                                    <label for="InsuranceNeeded">
                                        <input id="InsuranceNeeded" type="checkbox" v-model="localTravelOptions.insurance">
                                        {{ $t('btm.travel_options.insurance.is_insurance_needed') }}
                                    </label>
                                </div>

                                <div class="col-12 mt-4 mb-2">
                                    <div class="text-blue-600 font-weight-bold">
                                        {{ $t('taxi.pages.options.comments') }}
                                    </div>

                                    <textarea
                                        class="form-control w-100 pretty-input"
                                        rows="5"
                                        v-model="comments"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row mb-4">
                        <div class="col-12 px-4">
                            <button type="submit" class="btn btn-primary px-4 ml-3">
                                {{ $t('next') }}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import ProgressHeader from './components/ProgressHeader';
    import toastr from 'toastr';

    export default {
        components: {
            ProgressHeader,
        },

        data() {
            return {
                localTravelOptions: {},

                isPlaneTicketNeeded: false,
                isAirportTransferNeeded: false,
                isHotelNeeded: false,
                isInsuranceNeeded: false,
                isFerryTicketNeeded: false,
                isTrainTicketNeeded: false,
                isCoachTicketNeeded: false,
                isFormalAssistanceNeeded: false,
                isEventNeeded: false,
            };
        },

        computed: {
            ...mapGetters({
                travelOptions: 'businessTravelManagement/getTravelOptions',
            }),

            comments: {
                get() {
                    return this.$store.getters['businessTravelManagement/getTravelOptionComments'];
                },

                set(value) {
                    this.$store.dispatch('businessTravelManagement/setTravelOptionComments', value);
                },
            },
        },

        created() {
            this.localTravelOptions = this.travelOptions;
            this.isPlaneTicketNeeded = !!this.travelOptions.plane_option;
            this.isAirportTransferNeeded = !!this.travelOptions.transfer_option;
            this.isHotelNeeded = this.travelOptions.hotel_option && !!this.travelOptions.hotel_option.hotel_stars;
            this.isInsuranceNeeded = !!this.travelOptions.insurance;
            this.isFerryTicketNeeded = this.travelOptions.ferry_option && !!this.travelOptions.ferry_option.start_place;
            this.isTrainTicketNeeded = this.travelOptions.train_option && !!this.travelOptions.train_option.start_place;
            this.isCoachTicketNeeded = this.travelOptions.coach_option && !!this.travelOptions.coach_option.start_place;
            this.isFormalAssistanceNeeded = !!this.travelOptions.formal_assistance;
            this.isEventNeeded =  !!this.travelOptions.event_assistance;
        },

        methods: {
            handleSubmit() {
                if (!this.isPlaneTicketNeeded) {
                    this.localTravelOptions.plane_option = null;
                } else {
                    if (!this.localTravelOptions.plane_option) {
                        toastr.error(this.$t('field_is_required', {field: this.$t('btm.fields.plane_option')}));

                        return;
                    }
                }

                if (!this.isAirportTransferNeeded) {
                    this.localTravelOptions.transfer_option = null;
                } else {
                    if (!this.localTravelOptions.transfer_option) {
                        toastr.error(this.$t('field_is_required', {field: this.$t('btm.fields.transfer_option')}));

                        return;
                    }
                }

                if (!this.isHotelNeeded) {
                    this.localTravelOptions.hotel_option = {
                        hotel_preference: null,
                        hotel_stars: null,
                    };
                } else {
                    if (!this.localTravelOptions.hotel_option.hotel_stars) {
                        toastr.error(this.$t('field_is_required', {field: this.$t('btm.fields.hotel_stars')}));

                        return;
                    }
                    if (!this.localTravelOptions.hotel_option.hotel_preference) {
                        toastr.error(this.$t('field_is_required', {field: this.$t('btm.fields.hotel_preference')}));

                        return;
                    }
                }

                if (!this.isFerryTicketNeeded) {
                    this.localTravelOptions.ferry_option = {
                        start_place: null,
                        end_place: null,
                    };
                } else {
                    if (!this.localTravelOptions.ferry_option.start_place) {
                        toastr.error(this.$t('field_is_required', {field: this.$t('btm.fields.ferry_start_place')}));

                        return;
                    }
                    if (!this.localTravelOptions.ferry_option.end_place) {
                        toastr.error(this.$t('field_is_required', {field: this.$t('btm.fields.ferry_end_place')}));

                        return;
                    }
                }

                if (!this.isTrainTicketNeeded) {
                    this.localTravelOptions.train_option = {
                        start_place: null,
                        end_place: null,
                    };
                } else {
                    if (!this.localTravelOptions.train_option.start_place) {
                        toastr.error(this.$t('field_is_required', {field: this.$t('btm.fields.train_start_place')}));

                        return;
                    }
                    if (!this.localTravelOptions.train_option.end_place) {
                        toastr.error(this.$t('field_is_required', {field: this.$t('btm.fields.train_end_place')}));

                        return;
                    }
                }


                if (!this.isCoachTicketNeeded) {
                    this.localTravelOptions.coach_option = {
                        start_place: null,
                        end_place: null,
                    };
                } else {
                    if (!this.localTravelOptions.coach_option.start_place) {
                        toastr.error(this.$t('field_is_required', {field: this.$t('btm.fields.coach_start_place')}));

                        return;
                    }
                    if (!this.localTravelOptions.coach_option.end_place) {
                        toastr.error(this.$t('field_is_required', {field: this.$t('btm.fields.coach_end_place')}));

                        return;
                    }
                }

                this.$store.dispatch('businessTravelManagement/setTravelOptions', this.localTravelOptions);
                this.$store.dispatch('businessTravelManagement/setIsTravelOptionsComplete', true);

                this.$router.push({ name: 'business-travel-management.contact' });
            },
        },
    };
</script>

<style scoped>
    h4 {
        font-size: 1.2rem;
        letter-spacing: 0.02rem;
        padding: 16px 22px;
        border-bottom: 1px solid #F2F2F2;
    }

    .card-content * {
        color: #333333;
    }

    button {
        color: #ffffff !important;
        font-size: 1.2rem;
    }
</style>
